import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { useSurfaceManager } from '@/context/SurfaceContext';
import { WEB_ORIGIN } from '@/utils/webOrigin';

type NavigateFn = (opts: { to: string; replace?: boolean }) => void;

/**
 * Android hardware back, and deep links that arrive while the app is running.
 *
 * @capacitor/app's native side registers a back callback regardless of whether
 * JS listens. With no JS listener it goes back when the WebView has history
 * and otherwise *consumes the press and does nothing* — so at the app root the
 * back button appears broken, which Play reviewers do notice. Registering a
 * listener means we own the whole behaviour, exitApp() included.
 *
 * Back priority, matching what the button means on Android:
 *   1. A modal, sheet or panel is open  → close the topmost one.
 *   2. There is somewhere to go back to → go there.
 *   3. Otherwise                        → background the app.
 *
 * Step 1 delegates to SurfaceContext's escape stack rather than tracking
 * overlays separately, so back and Escape can't drift apart. The scan overlay
 * pushes its own history entry and is handled by step 2 — it deliberately owns
 * its back behaviour, and going through history keeps its popstate contract
 * intact.
 */
export function useNativeNavigation(navigate: NavigateFn): void {
  const { closeTopSurface } = useSurfaceManager();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listeners: { remove: () => void }[] = [];
    let cancelled = false;

    // addListener is async; a fast unmount could otherwise leak a listener
    // that outlives this effect.
    const track = (p: Promise<{ remove: () => void }>) => {
      p.then((l) => {
        if (cancelled) l.remove();
        else listeners.push(l);
      }).catch(() => {});
    };

    track(
      CapApp.addListener('backButton', ({ canGoBack }) => {
        if (closeTopSurface()) return;
        if (canGoBack) {
          window.history.back();
          return;
        }
        CapApp.exitApp();
      }),
    );

    // Without this, a tapped ravecave.app link that resolves to the app opens
    // it at whatever screen it was last on and silently drops the path. Only
    // our own origin is honoured — an intent can carry any URL, and this value
    // is turned straight into in-app navigation.
    track(
      CapApp.addListener('appUrlOpen', ({ url }) => {
        let parsed: URL;
        try {
          parsed = new URL(url);
        } catch {
          return;
        }
        if (parsed.origin !== WEB_ORIGIN) return;

        const path = `${parsed.pathname}${parsed.search}`;
        if (!path.startsWith('/')) return;
        navigate({ to: path });
      }),
    );

    return () => {
      cancelled = true;
      listeners.forEach((l) => l.remove());
    };
  }, [closeTopSurface, navigate]);
}
